import type { DisconnectStripeResponse, StripeConnectionStatusResponse } from '@jq4race/shared';
import { request } from './client.js';

// ── Billing — Stripe Connect API client (JQ4-35 · US-PAGOS-001) ──────────────

/**
 * Starts (or resumes) Stripe Connect onboarding via an Account Link.
 *
 * The backend GET /billing/stripe/connect returns JSON `{ url }` with the Stripe
 * authorize URL. The caller navigates the browser to it.
 *
 * A 302 redirect can't be used here: a cross-origin `fetch` returns an opaque
 * response that hides the Location header, and a plain browser navigation can't
 * send the Bearer token this endpoint requires.
 *
 * Typical usage: window.location.href = await startStripeConnect()
 */
export async function startStripeConnect(): Promise<string> {
  const { url } = await request<{ url: string }>('/billing/stripe/connect', { method: 'GET' });
  return url;
}

/**
 * Returns the Stripe connection status for the current organization.
 * GET /billing/stripe/status
 */
export async function getStripeConnectionStatus(): Promise<StripeConnectionStatusResponse> {
  return request<StripeConnectionStatusResponse>('/billing/stripe/status', { method: 'GET' });
}

/**
 * Disconnects the Stripe account for the current organization.
 * POST /billing/stripe/disconnect
 */
export async function disconnectStripe(): Promise<DisconnectStripeResponse> {
  return request<DisconnectStripeResponse>('/billing/stripe/disconnect', { method: 'POST' });
}
